import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from 'app/app.reducers';
import { Property } from 'app/internal/property/shared/models';
import { getSelectedProperty } from 'app/internal/property/property-store/property.selectors';
import { getWorkspaceSettings } from 'app/internal/workspace/workspace-store/workspace.selectors';
import { ModalComponent } from 'app/internal/shared';
import { getUserRole } from 'app/shared/auth/redux/auth.selectors';
import { CommOperation, OperationState } from 'app/shared/models';
import { DxDataGridComponent } from 'devextreme-angular';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil, tap } from 'rxjs/operators';
import { CLUB_SUBSCRIPTION_PLAN_ID } from '../../constants/club-subscription-plan-id';
import { MembershipLevelImportModalComponent } from '../../components/membership-level-import-modal/membership-level-import-modal.component';
import { DueFrequency, MembershipLevel, MembershipLevelStatus } from '../../models/membership-level.model';
import { formatMembershipLevelApiError } from '../../services/membership-level.service';
import { MembershipActions } from '../../store/membership.actions';
import {
  selectMembershipLevels,
  selectMembershipLevelsLoading,
} from '../../store/membership.selectors';

@Component({
  selector: 'sp-membership-levels',
  templateUrl: './membership-levels.component.html',
  styleUrls: ['./membership-levels.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MembershipLevelsComponent implements OnInit, OnDestroy {
  private static readonly DELETE_BLOCKED_MESSAGE =
    'This level still has active members assigned. Remove or reassign members before deleting.';

  @ViewChild('levelsGrid', { static: false }) levelsGrid: DxDataGridComponent;
  @ViewChild('editorModal') editorModal: ModalComponent;
  @ViewChild('deleteConfirmModal') deleteConfirmModal: ModalComponent;
  @ViewChild('bulkStatusModal') bulkStatusModal: ModalComponent;
  @ViewChild('bulkDeleteModal') bulkDeleteModal: ModalComponent;
  @ViewChild('levelImportModal', { static: true }) levelImportModal: MembershipLevelImportModalComponent;

  membershipLevelForm: FormGroup;

  /** Client-only toolbar filter; grid header filters apply on this list. */
  filteredLevels: MembershipLevel[] = [];
  filterText = '';
  loading = false;
  propertyId: string | null = null;
  /** Local copy of store list for grid + client-side filter. */
  levels: MembershipLevel[] = [];

  editorTitle = '';
  editing: MembershipLevel | null = null;

  readonly frequencyOptions: DueFrequency[] = ['Annual', 'Quarterly', 'Monthly'];
  readonly statusOptions: MembershipLevelStatus[] = ['Active', 'Retired', 'Planning'];

  /** DevExtreme selection keys (membershipCategoryId). */
  selectedRowKeys: string[] = [];
  toastIsVisible = false;
  bulkTargetStatus: MembershipLevelStatus = 'Active';
  userRole: string | null = null;

  /** Same role set as Accounts / other dashboards for import, add, row actions. */
  readonly membershipManageRoles: string[] = ['Admin', 'Manager', 'PortfolioAdmin'];

  private pendingDeleteId: string | null = null;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly fb: FormBuilder,
    private readonly store: Store<AppState>,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.membershipLevelForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern(/\S/)]],
      description: ['', [Validators.pattern(/\S/)]],
      duesFrequency: ['Monthly' as DueFrequency],
      duesAmount: [0, [Validators.min(0)]],
      activeMembers: [0, [Validators.min(0)]],
      status: ['Active' as MembershipLevelStatus],
    });
  }

  get canBulkAct(): boolean {
    const r = this.userRole;
    return r === 'Admin' || r === 'Manager' || r === 'PortfolioAdmin';
  }

  get numberOfSelectedRows(): number {
    return this.selectedRowKeys.length;
  }

  get allSelectedAreRetired(): boolean {
    if (this.selectedRowKeys.length === 0) {
      return false;
    }
    for (const id of this.selectedRowKeys) {
      const row = this.levels.find(l => l.membershipCategoryId === id);
      if (!row || row.status !== 'Retired') {
        return false;
      }
    }
    return true;
  }

  ngOnInit(): void {
    this.store
      .select(selectMembershipLevels)
      .pipe(takeUntil(this.destroy$))
      .subscribe(levels => {
        this.levels = levels;
        this.applyFilter();
        this.cdr.markForCheck();
      });

    this.store
      .select(selectMembershipLevelsLoading)
      .pipe(takeUntil(this.destroy$))
      .subscribe(loadingLevels => {
        this.loading = loadingLevels;
        this.cdr.markForCheck();
      });

    this.actions$
      .pipe(
        ofType(MembershipActions.loadLevelsSuccess),
        takeUntil(this.destroy$),
        tap(() => this.clearGridSelection()),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(MembershipActions.createLevelSuccess, MembershipActions.updateLevelSuccess),
        takeUntil(this.destroy$),
        tap(action => {
          this.editorModal?.hide();
          this.cancelEditor();
          if (action.type === MembershipActions.createLevelSuccess.type) {
            this.toastr.success('Membership Level created.', 'Success');
          } else {
            this.toastr.success('Membership Level updated.', 'Success');
          }
        }),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(MembershipActions.deleteLevelSuccess),
        takeUntil(this.destroy$),
        tap(() => {
          this.pendingDeleteId = null;
          this.deleteConfirmModal?.hide();
          this.toastr.success('Membership Level removed.', 'Success');
        }),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(MembershipActions.deleteLevelBlocked),
        takeUntil(this.destroy$),
        tap(() => {
          this.pendingDeleteId = null;
          this.deleteConfirmModal?.hide();
          this.toastr.error(MembershipLevelsComponent.DELETE_BLOCKED_MESSAGE, 'Cannot delete');
        }),
      )
      .subscribe();

    this.actions$
      .pipe(
        ofType(MembershipActions.deleteLevelFailure),
        takeUntil(this.destroy$),
        tap(({ error }) => {
          this.pendingDeleteId = null;
          const msg = formatMembershipLevelApiError(error, 'Could not remove membership level.');
          this.toastr.error(msg, 'Error');
        }),
      )
      .subscribe();

    this.store
      .select(getUserRole)
      .pipe(takeUntil(this.destroy$))
      .subscribe(role => {
        this.userRole = role;
        this.cdr.markForCheck();
      });

    this.store
      .select(getWorkspaceSettings)
      .pipe(
        filter(ws => ws != null),
        takeUntil(this.destroy$),
      )
      .subscribe(ws => {
        const planId = ws.subscriptionPlanId != null ? ws.subscriptionPlanId : 1;
        if (planId !== CLUB_SUBSCRIPTION_PLAN_ID) {
          void this.router.navigate(['/useraccount/properties/dashboard']);
          return;
        }
        this.cdr.markForCheck();
      });

    this.store
      .select(getSelectedProperty)
      .pipe(
        filter((p): p is Property => p != null),
        distinctUntilChanged((a, b) => a.propertyId === b.propertyId),
        takeUntil(this.destroy$),
      )
      .subscribe(p => {
        this.propertyId = p.propertyId;
        this.levelImportModal.propertyId = p.propertyId;
        this.dispatchReloadLevels();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  openAdd(): void {
    this.editing = null;
    this.editorTitle = 'Add New Membership Level';
    this.membershipLevelForm.reset({
      name: '',
      description: '',
      duesFrequency: 'Monthly',
      duesAmount: 0,
      activeMembers: 0,
      status: 'Active',
    });
    this.editorModal?.show();
  }

  openEdit(row: MembershipLevel): void {
    this.editing = { ...row };
    this.editorTitle = 'Edit Membership Level';
    this.membershipLevelForm.patchValue({
      name: row.name,
      description: row.description,
      duesFrequency: row.duesFrequency,
      duesAmount: row.duesAmount,
      activeMembers: row.activeMembers,
      status: row.status,
    });
    this.editorModal?.show();
  }

  cancelEditor(): void {
    this.editing = null;
    this.membershipLevelForm.reset({
      name: '',
      description: '',
      duesFrequency: 'Monthly',
      duesAmount: 0,
      activeMembers: 0,
      status: 'Active',
    });
  }

  saveEditor(): void {
    if (!this.propertyId) {
      return;
    }

    // Normalize inputs before validating so whitespace-only values don't pass `required`.
    const raw = this.membershipLevelForm.getRawValue();
    this.membershipLevelForm.patchValue(
      {
        name: ((raw.name as string) ?? '').trim(),
        description: ((raw.description as string) ?? '').trim(),
      },
      { emitEvent: false },
    );
    this.membershipLevelForm.updateValueAndValidity({ emitEvent: false });

    this.membershipLevelForm.markAllAsTouched();
    if (this.membershipLevelForm.invalid) {
      return;
    }
    const v = this.membershipLevelForm.getRawValue();
    const name = (v.name as string) ?? '';
    const description = (v.description as string) ?? '';
    const dues = (v.duesAmount as number) ?? 0;
    const members = Math.max(0, Math.floor(Number(v.activeMembers) || 0));
    const duesFrequency = v.duesFrequency as DueFrequency;
    const status = v.status as MembershipLevelStatus;

    if (this.editing) {
      const updated: MembershipLevel = {
        ...this.editing,
        name,
        description,
        duesFrequency,
        duesAmount: dues,
        activeMembers: members,
        status,
      };
      this.store.dispatch(MembershipActions.updateLevel({ propertyId: this.propertyId, level: updated }));
      return;
    }

    this.store.dispatch(
      MembershipActions.createLevel({
        propertyId: this.propertyId,
        draft: {
          name,
          description,
          duesFrequency,
          duesAmount: dues,
          activeMembers: members,
          status,
        },
      }),
    );
  }

  isFieldInvalid(fieldName: string): boolean {
    const field = this.membershipLevelForm.get(fieldName);
    if (!field || !field.invalid) {
      return false;
    }
    return !!(field.dirty || field.touched);
  }

  getErrorMessage(fieldName: string): string {
    const field = this.membershipLevelForm.get(fieldName);
    if (!field || !field.errors) {
      return '';
    }
    const errors = field.errors;
    const label = this.getFieldDisplayName(fieldName);
    if (errors['required']) {
      return `${label} is required`;
    }
    if (errors['pattern'] && (fieldName === 'description' || fieldName === 'name')) {
      return `${label} cannot be blank`;
    }
    if (errors['min']) {
      return `${label} must be at least ${errors['min'].min}`;
    }
    return '';
  }

  private getFieldDisplayName(fieldName: string): string {
    const map: Record<string, string> = {
      name: 'Name',
      description: 'Description',
      duesFrequency: 'Due frequency',
      duesAmount: 'Dues amount',
      activeMembers: 'Active member count',
      status: 'Status',
    };
    return map[fieldName] ?? fieldName;
  }

  requestDelete(row: MembershipLevel): void {
    this.pendingDeleteId = row.membershipCategoryId;
    if (row.activeMembers > 0) {
      this.pendingDeleteId = null;
      this.toastr.error(MembershipLevelsComponent.DELETE_BLOCKED_MESSAGE, 'Cannot delete');
      return;
    }
    this.deleteConfirmModal?.show();
  }

  confirmDelete(): void {
    if (!this.propertyId || !this.pendingDeleteId) {
      return;
    }
    this.store.dispatch(
      MembershipActions.deleteLevel({
        propertyId: this.propertyId,
        membershipCategoryId: this.pendingDeleteId,
      }),
    );
  }

  openImport(): void {
    this.levelImportModal?.openModal();
  }

  onImportUploaded(comm: CommOperation | null | undefined): void {
    if (!comm) {
      return;
    }
    if (comm.operationState === OperationState.success) {
      const count = comm.importedCount;
      if (count === 0) {
        this.toastr.info('No rows were imported.', 'Import complete');
      } else if (count === 1) {
        this.toastr.success('1 membership level imported successfully.', 'Import complete');
      } else {
        this.toastr.success(`${count} membership levels imported successfully.`, 'Import complete');
      }
      this.dispatchReloadLevels();
      return;
    }
    // Failed: errors shown in modal; still refresh list if anything could have changed
    this.dispatchReloadLevels();
  }

  statusClass(status: MembershipLevelStatus): string {
    switch (status) {
      case 'Active':
        return 'ml-badge ml-badge-active';
      case 'Retired':
        return 'ml-badge ml-badge-retired';
      default:
        return 'ml-badge ml-badge-planning';
    }
  }

  statusIconClass(status: MembershipLevelStatus): string {
    switch (status) {
      case 'Active':
        return 'ml-badge-icon fa-solid fa-check';
      case 'Retired':
        return 'ml-badge-icon fa-solid fa-triangle-exclamation';
      default:
        return 'ml-badge-icon fa-solid fa-calendar-day';
    }
  }

  onLevelsGridSelectionChanged(e: SelectionChangedEvent): void {
    // DevExtreme often fires outside Angular's zone; run inside the zone + detectChanges so *ngIf bulk bar updates.
    this.ngZone.run(() => {
      const fromEvent = e.selectedRowKeys as unknown[] | undefined;
      const fromGrid = e.component?.getSelectedRowKeys?.() as unknown[] | undefined;
      const raw = (fromEvent ?? fromGrid ?? []) as unknown[];
      const keys = Array.isArray(raw) ? raw : [];
      this.selectedRowKeys = this.normalizeSelectionKeys(keys);
      this.toastIsVisible = this.canBulkAct && this.selectedRowKeys.length > 0;
      this.cdr.markForCheck();
    });
  }

  /** DevExtreme may return key objects; `levels` lookup and bulk API need string ids. */
  private normalizeSelectionKeys(raw: unknown[]): string[] {
    return raw.map(k => {
      if (k != null && typeof k === 'object' && 'membershipCategoryId' in (k as Record<string, unknown>)) {
        return String((k as { membershipCategoryId: unknown }).membershipCategoryId);
      }
      return String(k);
    });
  }

  onLevelsGridCellPrepared(e: { rowType?: string; column?: { command?: string }; cellElement?: HTMLElement }): void {
    if (!this.canBulkAct && e.rowType === 'data' && e.column?.command === 'select' && e.cellElement) {
      const cb = e.cellElement.querySelector('.dx-select-checkbox') as HTMLElement | null;
      if (cb) {
        cb.style.display = 'none';
      }
    }
  }

  cancelBulkActionsToast(): void {
    this.clearGridSelection();
  }

  openBulkChangeStatus(): void {
    this.bulkTargetStatus = 'Active';
    this.bulkStatusModal?.show();
  }

  confirmBulkStatus(): void {
    if (!this.propertyId || this.selectedRowKeys.length === 0) {
      return;
    }
    const membershipCategoryIds = [...this.selectedRowKeys];
    this.bulkStatusModal?.hide();
    this.clearGridSelection();
    this.store.dispatch(
      MembershipActions.bulkUpdateStatus({
        propertyId: this.propertyId,
        membershipCategoryIds,
        status: this.bulkTargetStatus,
      }),
    );
  }

  openBulkDelete(): void {
    if (!this.allSelectedAreRetired) {
      return;
    }
    this.bulkDeleteModal?.show();
  }

  confirmBulkDelete(): void {
    if (!this.propertyId || this.selectedRowKeys.length === 0 || !this.allSelectedAreRetired) {
      return;
    }
    const membershipCategoryIds = [...this.selectedRowKeys];
    this.bulkDeleteModal?.hide();
    this.clearGridSelection();
    this.store.dispatch(
      MembershipActions.bulkDeleteLevels({
        propertyId: this.propertyId,
        membershipCategoryIds,
      }),
    );
  }

  private clearGridSelection(): void {
    this.levelsGrid?.instance?.deselectAll();
    this.ngZone.run(() => {
      this.selectedRowKeys = [];
      this.toastIsVisible = false;
      this.cdr.markForCheck();
    });
  }

  onFilterValueChanged(e: { value?: string }): void {
    this.filterText = e.value ?? '';
    this.applyFilter();
  }

  private applyFilter(): void {
    const q = this.filterText.trim().toLowerCase();
    if (!q) {
      this.filteredLevels = [...this.levels];
    } else {
      this.filteredLevels = this.levels.filter(row => {
        const fields = [row.name, row.description, row.status, row.duesFrequency];
        return fields.some(text => String(text ?? '').toLowerCase().includes(q));
      });
    }
    this.cdr.markForCheck();
  }

  private dispatchReloadLevels(): void {
    if (!this.propertyId) {
      return;
    }
    this.store.dispatch(MembershipActions.loadLevels({ propertyId: this.propertyId }));
  }
}
